import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { ShieldCheck } from '@phosphor-icons/react';

export default function PrivacyPage() {
  const { language } = useLanguage();

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const content = {
    en: {
      title: 'Privacy Policy',
      subtitle: 'How we handle your information.',
      lastUpdated: 'Last updated: January 2026',
      intro: 'Your privacy matters to us. Here\'s a straightforward explanation of what we collect, why, and how we keep it safe.',
      sections: [
        {
          title: 'What we collect',
          content: 'When you use PaperPal, we collect information you share with us directly — like your name, email, and the details you provide during onboarding. If you upload documents, we store those too. We also collect basic usage data to understand how people use the app.'
        },
        {
          title: 'Why we collect it',
          content: 'Simple: to make PaperPal work for you. Your onboarding answers help us create a personalized checklist. Your documents are stored so you can access them later. Usage data helps us improve the product for everyone.'
        },
        {
          title: 'We don\'t sell your data',
          content: 'Period. We don\'t sell, trade, or rent your personal information to anyone. We might share data with service providers who help us run PaperPal (like our hosting provider), but only what\'s necessary and under strict agreements.'
        },
        {
          title: 'How we protect it',
          content: 'We use industry-standard security measures to protect your information. Your data is encrypted, access is restricted, and we regularly review our security practices. That said, no system is 100% secure — we do our best, but please be mindful of what you share online.'
        },
        {
          title: 'Your control',
          content: 'You can update or delete your account information anytime through your profile settings. Want a copy of your data or want us to delete everything? Just reach out and we\'ll help you.'
        },
        {
          title: 'Cookies',
          content: 'We use cookies to keep you logged in and remember your preferences (like your language choice). Nothing creepy — just the basics to make the app work smoothly.'
        }
      ],
      closing: 'Questions about your privacy? We\'re here to help.',
      contact: 'Contact us'
    },
    fr: {
      title: 'Politique de confidentialité',
      subtitle: 'Comment on gère vos informations.',
      lastUpdated: 'Dernière mise à jour : janvier 2026',
      intro: 'Votre vie privée nous tient à cœur. Voici une explication claire de ce qu\'on collecte, pourquoi, et comment on le protège.',
      sections: [
        {
          title: 'Ce qu\'on collecte',
          content: 'Quand vous utilisez PaperPal, on collecte les informations que vous partagez directement — comme votre nom, courriel, et les détails fournis lors de l\'inscription. Si vous téléchargez des documents, on les garde aussi. On collecte aussi des données d\'utilisation basiques pour comprendre comment les gens utilisent l\'appli.'
        },
        {
          title: 'Pourquoi on le collecte',
          content: 'Simple : pour que PaperPal fonctionne pour vous. Vos réponses d\'inscription nous aident à créer une checklist personnalisée. Vos documents sont stockés pour que vous puissiez y accéder plus tard. Les données d\'utilisation nous aident à améliorer le produit pour tout le monde.'
        },
        {
          title: 'On ne vend pas vos données',
          content: 'Point final. On ne vend, n\'échange ni ne loue vos informations personnelles à personne. On peut partager des données avec des fournisseurs qui nous aident à faire fonctionner PaperPal (comme notre hébergeur), mais seulement le nécessaire et sous des accords stricts.'
        },
        {
          title: 'Comment on les protège',
          content: 'On utilise des mesures de sécurité standard de l\'industrie pour protéger vos informations. Vos données sont chiffrées, l\'accès est restreint, et on révise régulièrement nos pratiques de sécurité. Cela dit, aucun système n\'est 100% sécurisé — on fait de notre mieux, mais soyez prudent avec ce que vous partagez en ligne.'
        },
        {
          title: 'Votre contrôle',
          content: 'Vous pouvez mettre à jour ou supprimer les informations de votre compte à tout moment dans vos paramètres de profil. Vous voulez une copie de vos données ou qu\'on supprime tout? Contactez-nous et on vous aidera.'
        },
        {
          title: 'Cookies',
          content: 'On utilise des cookies pour vous garder connecté et retenir vos préférences (comme votre choix de langue). Rien de louche — juste l\'essentiel pour que l\'appli fonctionne bien.'
        }
      ],
      closing: 'Des questions sur votre vie privée? On est là pour aider.',
      contact: 'Nous contacter'
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-pp-bg flex flex-col">
      <Header />
      
      <main className="flex-1 pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8 md:mb-12"
          >
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-pp-success-muted flex items-center justify-center mx-auto mb-4 md:mb-5">
              <ShieldCheck size={24} className="text-pp-success" weight="duotone" />
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-pp-text mb-3" data-testid="privacy-title">
              {t.title}
            </h1>
            <p className="text-pp-text-secondary text-base md:text-lg">
              {t.subtitle}
            </p>
            <p className="text-pp-text-muted text-sm mt-2">{t.lastUpdated}</p>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-6"
          >
            {/* Intro */}
            <p className="text-pp-text-secondary leading-relaxed text-base md:text-lg">
              {t.intro}
            </p>

            {/* Sections */}
            <div className="space-y-8 md:space-y-10">
              {t.sections.map((section, index) => (
                <div key={index}>
                  <h2 className="font-heading font-medium text-pp-text text-lg md:text-xl mb-3">
                    {section.title}
                  </h2>
                  <p className="text-pp-text-secondary text-sm md:text-base leading-relaxed">
                    {section.content}
                  </p>
                </div>
              ))}
            </div>

            {/* Closing */}
            <div className="pt-6 md:pt-8 border-t border-pp-border">
              <p className="text-pp-text-secondary mb-4">{t.closing}</p>
              <Link 
                to="/contact" 
                className="inline-flex items-center text-pp-navy font-medium hover:underline"
              >
                {t.contact} →
              </Link>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
